import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// Token storage (in-memory for security, persisted in sessionStorage for tab refresh)
function getStoredToken() {
  try { return sessionStorage.getItem("t2s_access_token"); } catch { return null; }
}
function storeToken(token) {
  try { if (token) sessionStorage.setItem("t2s_access_token", token); else sessionStorage.removeItem("t2s_access_token"); } catch {}
}
function getStoredRefresh() {
  try { return sessionStorage.getItem("t2s_refresh_token"); } catch { return null; }
}
function storeRefresh(token) {
  try { if (token) sessionStorage.setItem("t2s_refresh_token", token); else sessionStorage.removeItem("t2s_refresh_token"); } catch {}
}

// Axios instance with auth interceptor
const authAxios = axios.create({});

authAxios.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Export for use in other components
export { authAxios };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshAttempted = useRef(false);

  const checkAuth = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      // Try cookie-based auth as fallback
      try {
        const res = await axios.get(`${API}/auth/me`);
        setUser(res.data);
        setLoading(false);
        return;
      } catch {
        setUser(null);
        setLoading(false);
        return;
      }
    }

    try {
      const res = await authAxios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (err) {
      // Try refresh
      if (!refreshAttempted.current) {
        refreshAttempted.current = true;
        const refreshToken = getStoredRefresh();
        try {
          const refreshRes = await axios.post(`${API}/auth/refresh`, {}, {
            headers: refreshToken ? { Authorization: `Bearer ${refreshToken}` } : {},
          });
          if (refreshRes.data?.access_token) {
            storeToken(refreshRes.data.access_token);
          }
          const res = await authAxios.get(`${API}/auth/me`);
          setUser(res.data);
        } catch {
          storeToken(null);
          storeRefresh(null);
          setUser(null);
        }
      } else {
        storeToken(null);
        storeRefresh(null);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData) => {
    // Store tokens from login/register response
    if (userData?.access_token) {
      storeToken(userData.access_token);
      storeRefresh(userData.refresh_token);
    }
    // Clean token fields before setting user
    const { access_token, refresh_token, ...cleanUser } = userData || {};
    setUser(cleanUser);
  };

  const logout = async () => {
    try {
      await authAxios.post(`${API}/auth/logout`);
    } catch { /* ignore */ }
    storeToken(null);
    storeRefresh(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
